import React, {useEffect, useRef, useState, useCallback} from 'react';
import {createPortal} from 'react-dom';
import {Device} from '@capacitor/device';
import {MediaFile} from '../types';
import {downloadFile} from '../libraries/media';
import DownloadProgress from './DownloadProgress';

interface MediaViewerProps {
    media: MediaFile[];
    startIndex: number;
    onClose: () => void;
}

const SLIDE_GAP = 20;

const MediaViewer: React.FC<MediaViewerProps> = ({media, startIndex, onClose}) => {
    const [currentIndex, setCurrentIndex] = useState(startIndex);
    const [chromeVisible, setChromeVisible] = useState(true);
    const [downloadProgress, setDownloadProgress] = useState(0);
    const [downloadBytes, setDownloadBytes] = useState(0);
    const [downloadTotal, setDownloadTotal] = useState(0);
    const [isDownloading, setIsDownloading] = useState(false);
    const [downloadError, setDownloadError] = useState<string | undefined>();
    const [downloadComplete, setDownloadComplete] = useState(false);
    const [devicePlatform, setDevicePlatform] = useState<string | null>(null);
    const total = media.length;

    const stripRef = useRef<HTMLDivElement>(null);
    const viewportWidth = useRef(window.innerWidth);

    // horizontal drag
    const dragRef = useRef({
        active: false,
        startX: 0,
        startY: 0,
        offsetX: 0,
        settled: false,
        direction: '' as '' | 'h' | 'v',
        startTime: 0,
    });

    // vertical dismiss
    const dismissRef = useRef({active: false, deltaY: 0});

    const lastTapRef = useRef(0);

    const slideWidth = () => viewportWidth.current + SLIDE_GAP;
    const baseOffset = (idx: number) => -idx * slideWidth();

    const current = media[currentIndex];
    const isVideo = current?.mimetype?.startsWith('video/');

    const applyStripTransform = useCallback((offset: number, animate: boolean) => {
        if (!stripRef.current) return;
        stripRef.current.style.transition = animate ? 'transform 0.35s cubic-bezier(.25,.1,.25,1)' : 'none';
        stripRef.current.style.transform = `translate3d(${offset}px, 0, 0)`;
    }, []);

    const settleTo = useCallback((idx: number) => {
        const clamped = Math.max(0, Math.min(total - 1, idx));
        setCurrentIndex(clamped);
        applyStripTransform(baseOffset(clamped), true);
    }, [total, applyStripTransform]);

    // init position
    useEffect(() => {
        viewportWidth.current = window.innerWidth;
        applyStripTransform(baseOffset(currentIndex), false);
        const onResize = () => {
            viewportWidth.current = window.innerWidth;
            applyStripTransform(baseOffset(currentIndex), false);
        };
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // detect device platform for iOS-specific behavior
    useEffect(() => {
        const detectPlatform = async () => {
            try {
                const info = await Device.getInfo();
                setDevicePlatform(info.platform);
            } catch (error) {
                console.error('Failed to detect device platform:', error);
            }
        };
        void detectPlatform();
    }, []);

    // keyboard & body lock
    useEffect(() => {
        const orig = {overflow: document.body.style.overflow, bg: document.body.style.backgroundColor};
        document.body.style.overflow = 'hidden';
        document.body.style.backgroundColor = 'black';
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
            if (e.key === 'ArrowRight') settleTo(currentIndex + 1);
            if (e.key === 'ArrowLeft') settleTo(currentIndex - 1);
        };
        window.addEventListener('keydown', onKey);
        return () => {
            window.removeEventListener('keydown', onKey);
            document.body.style.overflow = orig.overflow;
            document.body.style.backgroundColor = orig.bg;
        };
    }, [onClose, settleTo, currentIndex]);

    // ── touch handlers ──
    const onTouchStart = useCallback((e: React.TouchEvent) => {
        // Ignore touches that start on viewer controls (close/download/etc.)
        const target = e.target as HTMLElement | null;
        if (target?.closest('[data-media-viewer-control="true"]')) return;

        if (e.touches.length !== 1) return;

        const touch = e.touches[0];
        const now = Date.now();
        dragRef.current = {
            active: true,
            startX: touch.clientX,
            startY: touch.clientY,
            offsetX: baseOffset(currentIndex),
            settled: false,
            direction: '',
            startTime: now,
        };
        dismissRef.current = {active: false, deltaY: 0};
    }, [currentIndex]);

    const onTouchMove = useCallback((e: React.TouchEvent) => {
        if (!dragRef.current.active || e.touches.length !== 1) return;
        const touch = e.touches[0];
        const dx = touch.clientX - dragRef.current.startX;
        const dy = touch.clientY - dragRef.current.startY;

        // decide direction once
        if (!dragRef.current.settled) {
            if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
            dragRef.current.settled = true;
            dragRef.current.direction = Math.abs(dx) >= Math.abs(dy) ? 'h' : 'v';
        }

        if (dragRef.current.direction === 'h') {
            let newOffset = dragRef.current.offsetX + dx;
            // rubber-band at edges
            if (currentIndex === 0 && dx > 0) newOffset = dragRef.current.offsetX + dx * 0.3;
            if (currentIndex === total - 1 && dx < 0) newOffset = dragRef.current.offsetX + dx * 0.3;
            applyStripTransform(newOffset, false);
        } else if (dragRef.current.direction === 'v') {
            dismissRef.current = {active: true, deltaY: dy};
            const absDy = Math.abs(dy);
            const progress = Math.min(absDy / 300, 1);
            const itemScale = 1 - progress * 0.25;
            const opacity = 1 - progress * 0.6;
            const slideEl = stripRef.current?.children[currentIndex] as HTMLElement | undefined;
            if (slideEl) {
                slideEl.style.transition = 'none';
                slideEl.style.transform = `translate3d(0, ${dy}px, 0) scale(${itemScale})`;
                slideEl.style.opacity = `${opacity}`;
            }
        }
    }, [currentIndex, total, applyStripTransform]);

    const onTouchEnd = useCallback((e: React.TouchEvent) => {
        // Ignore touches that end on viewer controls (close/download/etc.)
        const target = e.target as HTMLElement | null;
        if (target?.closest('[data-media-viewer-control="true"]')) {
            dragRef.current.active = false;
            dismissRef.current = {active: false, deltaY: 0};
            return;
        }

        if (!dragRef.current.active) return;
        const wasSettled = dragRef.current.settled;
        const dir = dragRef.current.direction;
        dragRef.current.active = false;

        // vertical dismiss
        if (dismissRef.current.active) {
            const absDy = Math.abs(dismissRef.current.deltaY);
            const slideEl = stripRef.current?.children[currentIndex] as HTMLElement | undefined;
            if (absDy > 100) {
                if (slideEl) {
                    slideEl.style.transition = 'transform 0.25s ease, opacity 0.25s ease';
                    slideEl.style.transform = `translate3d(0, ${dismissRef.current.deltaY > 0 ? 400 : -400}px, 0) scale(0.6)`;
                    slideEl.style.opacity = '0';
                }
                setTimeout(onClose, 250);
            } else {
                if (slideEl) {
                    slideEl.style.transition = 'transform 0.3s cubic-bezier(.25,.1,.25,1), opacity 0.3s ease';
                    slideEl.style.transform = 'translate3d(0, 0, 0) scale(1)';
                    slideEl.style.opacity = '1';
                }
            }
            dismissRef.current = {active: false, deltaY: 0};
            return;
        }

        // horizontal swipe
        if (dir === 'h') {
            const touch = e.changedTouches[0];
            const dx = touch.clientX - dragRef.current.startX;
            const elapsed = Date.now() - dragRef.current.startTime;
            const velocity = Math.abs(dx) / Math.max(elapsed, 1);
            let targetIndex = currentIndex;
            if (Math.abs(dx) > viewportWidth.current * 0.2 || velocity > 0.5) {
                if (dx < 0 && currentIndex < total - 1) targetIndex = currentIndex + 1;
                if (dx > 0 && currentIndex > 0) targetIndex = currentIndex - 1;
            }
            settleTo(targetIndex);
        }

        // single tap → toggle chrome (only if no drag happened)
        if (!wasSettled) {
            const now = Date.now();
            // simple debounce to avoid accidental toggles
            if (now - lastTapRef.current > 400) {
                setChromeVisible(v => !v);
            }
            lastTapRef.current = now;
        }
    }, [currentIndex, total, settleTo, onClose]);

    const goNext = useCallback(() => settleTo(currentIndex + 1), [settleTo, currentIndex]);
    const goPrev = useCallback(() => settleTo(currentIndex - 1), [settleTo, currentIndex]);

    const triggerDownload = useCallback(async () => {
        if (!current) return;

        // Pause video on iOS devices
        if (devicePlatform === 'ios' && isVideo) {
            const videoElements = stripRef.current?.querySelectorAll('video');
            if (videoElements && videoElements.length > 0) {
                const currentVideoElement = videoElements[currentIndex] as HTMLVideoElement;
                if (currentVideoElement) {
                    currentVideoElement.pause();
                }
            }
        }

        setIsDownloading(true);
        setDownloadError(undefined);
        setDownloadComplete(false);
        setDownloadProgress(0);
        setDownloadBytes(0);
        setDownloadTotal(0);

        try {
            await downloadFile(
                current.downloadUrl || current.url,
                current.caption,
                (bytes, contentLength) => {
                    console.log(`Download progress: ${bytes} / ${contentLength}`);
                    setDownloadBytes(bytes);
                    setDownloadTotal(contentLength);
                    const percentage = Math.round((bytes / contentLength) * 100);
                    setDownloadProgress(percentage);
                }
            );
            setDownloadComplete(true);
            setDownloadProgress(100);
        } catch (error) {
            const errorMsg = error instanceof Error ? error.message : 'Unbekannter Fehler';
            setDownloadError(errorMsg);
            console.error('Error downloading file:', error);
        } finally {
            setIsDownloading(false);
        }
    }, [current, devicePlatform, isVideo, currentIndex]);

    const handleDownloadClick = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        void triggerDownload();
    }, [triggerDownload]);

    const handleDownloadTouchEnd = useCallback((e: React.TouchEvent) => {
        e.preventDefault();
        e.stopPropagation();
        void triggerDownload();
    }, [triggerDownload]);

    const handleControlTouchStart = useCallback((e: React.TouchEvent) => {
        // Prevent control taps from initializing overlay drag/tap logic.
        e.stopPropagation();
    }, []);

    const closeDownloadDialog = useCallback(() => {
        setIsDownloading(false);
        setDownloadComplete(false);
        setDownloadError(undefined);
        setDownloadProgress(0);
        setDownloadBytes(0);
        setDownloadTotal(0);
    }, []);

    const handleCloseClick = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        onClose();
    }, [onClose]);

    const handleCloseTouchEnd = useCallback((e: React.TouchEvent) => {
        e.preventDefault();
        e.stopPropagation();
        onClose();
    }, [onClose]);

    const stopProp = (e: React.MouseEvent) => e.stopPropagation();
    const chromeClass = `transition-opacity duration-300 ${chromeVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'}`;

    return createPortal(
        <div
            className="fixed inset-0 z-[9999] bg-black select-none"
            style={{touchAction: 'none', scrollbarWidth: 'none'}}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
        >
            {/* Close */}
            <button
                onClick={handleCloseClick}
                onTouchStart={handleControlTouchStart}
                onTouchEnd={handleCloseTouchEnd}
                className={`absolute z-[10001] p-2 rounded-full bg-white/10 hover:bg-white/20 text-white backdrop-blur-md border border-white/10 ${chromeClass}`}
                style={{top: 'calc(1rem + env(safe-area-inset-top, 0px))', right: 'calc(1rem + env(safe-area-inset-right, 0px))'}}
                aria-label="Schließen"
                data-media-viewer-control="true"
            >
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                     stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
            </button>

            {/* Download */}
            {current && (
                <button
                    onClick={handleDownloadClick}
                    onTouchStart={handleControlTouchStart}
                    onTouchEnd={handleDownloadTouchEnd}
                    className={`absolute z-[10001] p-2 rounded-full bg-white/10 hover:bg-white/20 text-white backdrop-blur-md border border-white/10 ${chromeClass}`}
                    style={{top: 'calc(1rem + env(safe-area-inset-top, 0px))', right: 'calc(4rem + env(safe-area-inset-right, 0px))'}}
                    aria-label="Download"
                    data-media-viewer-control="true"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                         stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
                    </svg>
                </button>
            )}

            {/* Counter */}
            {total > 1 && (
                <div
                    className={`absolute z-[10001] text-white/80 text-sm font-medium px-3 py-1 ${chromeClass}`}
                    style={{top: 'calc(1.15rem + env(safe-area-inset-top, 0px))', left: '50%', transform: 'translateX(-50%)'}}
                >
                    {currentIndex + 1} / {total}
                </div>
            )}

            {/* Desktop arrows */}
            {currentIndex > 0 && (
                <button
                    onClick={(e) => { e.stopPropagation(); goPrev(); }}
                    className={`absolute left-3 z-[10001] p-2 rounded-full bg-black/40 hover:bg-white/20 text-white hidden sm:flex items-center justify-center ${chromeClass}`}
                    style={{top: '50%', transform: 'translateY(-50%)'}}
                    aria-label="Zurück"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none"
                         stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="15 18 9 12 15 6"/>
                    </svg>
                </button>
            )}
            {currentIndex < total - 1 && (
                <button
                    onClick={(e) => { e.stopPropagation(); goNext(); }}
                    className={`absolute right-3 z-[10001] p-2 rounded-full bg-black/40 hover:bg-white/20 text-white hidden sm:flex items-center justify-center ${chromeClass}`}
                    style={{top: '50%', transform: 'translateY(-50%)'}}
                    aria-label="Weiter"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none"
                         stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="9 18 15 12 9 6"/>
                    </svg>
                </button>
            )}

            {/* Caption */}
            {current?.caption && (
                <div
                    className={`absolute z-[10001] left-4 right-4 text-center text-white/80 text-sm ${chromeClass}`}
                    style={{bottom: 'calc(1.5rem + env(safe-area-inset-bottom, 0px))'}}
                >
                    {current.caption}
                </div>
            )}

            {/* Slide strip */}
            <div
                ref={stripRef}
                className="absolute inset-0 flex items-center"
                style={{width: `${total * slideWidth()}px`, willChange: 'transform'}}
            >
                {media.map((item, idx) => {
                    const itemIsVideo = item.mimetype?.startsWith('video/');
                    const isActive = idx === currentIndex;

                    return (
                        <div
                            key={item.id}
                            className="flex-shrink-0 flex items-center justify-center overflow-hidden"
                            style={{
                                width: `${viewportWidth.current}px`,
                                height: '100%',
                                marginRight: idx < total - 1 ? `${SLIDE_GAP}px` : undefined,
                            }}
                        >
                            {itemIsVideo ? (
                                <video
                                    key={`video-${item.id}-${isActive}`}
                                    src={item.url}
                                    controls={isActive}
                                    autoPlay={isActive}
                                    playsInline
                                    className="max-w-full max-h-full rounded-lg"
                                    poster={item.thumbnailUrl}
                                    style={{maxHeight: '90dvh'}}
                                    onClick={stopProp}
                                />
                            ) : (
                                <img
                                    src={item.url}
                                    alt={item.caption || item.filename}
                                    className="max-w-full max-h-full object-contain select-none"
                                    draggable={false}
                                />
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Download Progress Modal */}
            <DownloadProgress
                fileName={current?.filename || ''}
                isVisible={isDownloading || downloadComplete}
                progress={downloadProgress}
                bytes={downloadBytes}
                contentLength={downloadTotal}
                isComplete={downloadComplete}
                error={downloadError}
                onClose={closeDownloadDialog}
            />
        </div>,
        document.body,
    );
};

export default MediaViewer;

